import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import { useAppXrResource } from './useAppXrResource';
import type { SandboxXrStatus } from './sandboxXrTypes';
import { sandboxStorefrontUrl } from './sandboxXrTypes';

function StatusIcon({ ok }: { ok: boolean | undefined }) {
  if (ok === true)
    return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />;
  if (ok === false)
    return <ErrorIcon sx={{ color: '#f44336', fontSize: 18 }} />;
  return <HourglassEmptyIcon sx={{ color: '#ff9800', fontSize: 18 }} />;
}

const rowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  py: 0.5,
};
const labelSx = { color: 'text.secondary', fontSize: '0.85rem' };
const valueSx = { fontSize: '0.85rem', fontFamily: 'monospace' };

export const SandboxStatusCard = () => {
  const { resource, conditions, error, loading } = useAppXrResource();
  const status = resource?.status as SandboxXrStatus | undefined;

  if (error) {
    return (
      <Card>
        <CardHeader title="Sandbox Status" />
        <CardContent>
          <Typography color="error">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading || !status) {
    return (
      <Card>
        <CardHeader title="Sandbox Status" />
        <CardContent>
          <Typography>Loading...</Typography>
        </CardContent>
      </Card>
    );
  }

  const ready = conditions.find(c => c.type === 'Ready')?.status === 'True';
  const url = sandboxStorefrontUrl(status.dns?.domain);

  return (
    <Card>
      <CardHeader
        title="Sandbox Status"
        action={
          status.frozen ? (
            <Chip
              icon={<AcUnitIcon sx={{ fontSize: 14 }} />}
              label="Frozen"
              size="small"
              color="info"
              variant="outlined"
              sx={{ mt: 1, mr: 1, height: 22 }}
            />
          ) : undefined
        }
      />
      <CardContent>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Ready</Typography>
          <StatusIcon ok={ready} />
        </Box>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Project</Typography>
          <Typography sx={valueSx}>{status.projectId ?? '—'}</Typography>
        </Box>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Cluster</Typography>
          <Typography sx={valueSx}>{status.cluster?.name ?? '—'}</Typography>
        </Box>
        {url && (
          <Box sx={rowSx}>
            <Typography sx={labelSx}>Storefront</Typography>
            <Link href={url} target="_blank" rel="noopener" sx={valueSx}>
              {status.dns?.domain}
            </Link>
          </Box>
        )}
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Expires</Typography>
          <Typography sx={valueSx}>{status.expiresAt ?? '—'}</Typography>
        </Box>
        {status.owner && (
          <Box sx={rowSx}>
            <Typography sx={labelSx}>Owner</Typography>
            <Typography sx={valueSx}>{status.owner}</Typography>
          </Box>
        )}
        {conditions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={labelSx} gutterBottom>
              Conditions
            </Typography>
            <Grid container spacing={1}>
              {conditions.map(c => (
                <Grid item key={c.type}>
                  <Chip
                    sx={{ m: 0.25 }}
                    size="small"
                    icon={<StatusIcon ok={c.status === 'True'} />}
                    label={`${c.type}: ${c.message ?? c.reason ?? c.status}`}
                    variant="outlined"
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
